import { track } from '@amplitude/analytics-browser'
import { isHandledAccessError } from 'api/helpers/subdivision'
import { AxiosError } from 'axios'
import { i18n } from 'boot/i18n'

interface ErrorServiceConfig {
  readonly statusCodes: readonly number[]
}

export class ErrorHandlingService {
  readonly #config: ErrorServiceConfig = {
    statusCodes: [401, 408, 409, 422, 429, 500, 504],
  }

  readonly #toast: ReturnType<typeof useToast>

  constructor() {
    this.#toast = useToast()
  }

  handleError(error: unknown): void {
    // Отказ по правам или подразделению уже показан обработчиком axios.
    if (this.#isCanceledRequest(error) || isHandledAccessError(error)) {
      return
    }

    this.#trackErrorStatus(error)
    const message = this.#getErrorMessage(error)
    this.#notifyUser(message)
  }

  #isCanceledRequest(error: unknown): boolean {
    return error instanceof AxiosError && error.code === AxiosError.ERR_CANCELED
  }

  #getErrorMessage(error: unknown): string {
    if (!(error instanceof AxiosError) && error instanceof Error) {
      return this.#translateError(error.message || 'error.unknown')
    }

    if (error instanceof AxiosError) {
      if (error.code === AxiosError.ECONNABORTED) {
        return this.#translateError('error.timeout')
      }

      if (error.code === AxiosError.ERR_NETWORK) {
        return this.#translateError('error.network')
      }

      if (this.#isHandledStatus(error.status)) {
        return this.#translateError(`errorStatus.${error.status}`)
      }

      return (
        error.response?.data?.message || this.#translateError('error.unknown')
      )
    }

    return this.#translateError('error.unknown')
  }

  #isHandledStatus(status: number | undefined): status is number {
    return (
      typeof status === 'number' && this.#config.statusCodes.includes(status)
    )
  }

  #translateError(key: string): string {
    return i18n.global.t(key)
  }

  #notifyUser(message: string): void {
    this.#toast.add({
      description: message,
      color: 'error',
      icon: 'i-clarity-error-standard-line',
    })
  }

  #trackErrorStatus(error: unknown): void {
    if (error instanceof AxiosError && error.status && error.status >= 400) {
      track('error', { error_code: error.status })
    }
  }
}

const errorService = new ErrorHandlingService()

export const errorHandler = errorService.handleError.bind(errorService)
